<script setup async>
import CommissionNote from '~/components/marketing/CommissionNote.vue';
import MarketingBubbleHero from "~/components/marketing/MarketingBubbleHero.vue";
import FeatureRow from "~/components/marketing/FeatureRow.vue";
import AppFrame from "~/components/marketing/AppFrame.vue";
import AppCallout from "~/components/marketing/AppCallout.vue";
import AppHomePreview from "~/components/marketing/AppHomePreview.vue";
import StatsView from "~/components/stats/StatsView.vue";
import FixtureCell from "~/components/fixtures/FixtureCell.vue";
import LadderView from "~/components/ladders/LadderView.vue";
import LeagueCycleDivisionMatrix from "~/components/leagues/LeagueCycleDivisionMatrix.vue";
import BracketKnockoutStage from "~/components/tournaments/BracketKnockoutStage.vue";
import AmericanoStandingsTable from "~/components/americanos/AmericanoStandingsTable.vue";
import MeetOccurrenceCard from "~/components/meets/MeetOccurrenceCard.vue";
import TestimonialDisplay from "~/components/design/TestimonialDisplay.vue";
import DownloadAppBanner from "~/components/design/DownloadAppBanner.vue";
import SportsDisplay from "~/components/design/SportsDisplay.vue";

// Real app components rendered with shared demo data — the showcases below stay
// pixel-true to the live design. Nothing here is fetched or persisted.
const {
  leagueDivisionDemo,
  ladderDemo,
  heroFixtureDemo,
  playedFixtureDemo,
  bracketSemiDemo,
  bracketFinalDemo,
  americanoStandingsDemo,
  meetDemo,
  statsDemo,
} = useMarketingDemoData();

// SoftwareApplication structured data — helps rank for "league management
// software", "ladder app", "tournament management app" etc. without stuffing
// keywords into the visible hero.
useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Tap Leagues',
        applicationCategory: 'SportsApplication',
        operatingSystem: 'Web, iOS, Android',
        url: 'https://tapleagues.com',
        description: 'Box league, ladder, tournament, americano league and meet management software and app for running competitions in any head-to-head sport, for clubs, groups of friends, schools, workplaces and anyone who wants to compete. Players enter scores, opponents approve them, and standings, ladders and brackets update live.',
        featureList: [
          'League management software with box leagues and divisions',
          'Challenge ladder app with positions, freezes and expiry rules',
          'Tournament management app with knockout brackets and groups',
          'Americano leagues with solo entry and rotating doubles partners',
          'Meets: recurring social sessions with RSVPs and waitlists',
          'Live standings, matches, results and player stats',
        ],
        offers: {'@type': 'Offer', price: '0', priceCurrency: 'GBP'},
      }),
    },
  ],
});
</script>

<template>
  <v-app class="bg-light">

    <!-- ===== HERO — floating sport bubbles, competition-first ===== -->
    <marketing-bubble-hero
        eyebrow="For clubs, mates and everyone in between"
        title="A bit of healthy competition never hurt anyone."
        subtitle="Box leagues, ladders, tournaments, americano leagues and meets for any group that loves to compete. We handle the matches, scores and standings. You just turn up and play.">
      <template #actions>
        <v-btn color="primary" size="large" rounded="pill" class="px-7" to="/register" prepend-icon="mdi-sword-cross">
          Get started, it's free*
        </v-btn>
        <v-btn size="large" rounded="pill" class="px-7 mk-ghost" to="/features" prepend-icon="mdi-star-outline">
          See every feature
        </v-btn>
      </template>
      <template #note>Set up in minutes · no card required<commission-note/></template>
    </marketing-bubble-hero>

    <!-- ===== SPORTS — full-bleed support marquee ===== -->
    <section class="mk-sports">
      <sports-display/>
    </section>

    <v-container class="mk-page">

      <!-- Plain-English overview — natural keyword coverage, reads like a real descriptor -->
      <section class="mk-overview">
        <p>
          Tap Leagues is free <strong>league, ladder and tournament management software</strong> and an
          <strong>app for iOS and Android</strong> for running competitions in any head-to-head sport.
          Spin up a box league, a challenge ladder or a knockout tournament, share a link, and let the
          scores, standings and brackets keep themselves up to date.
        </p>
      </section>

      <!-- ===== FEATURES — real components, framed and angled ===== -->
      <feature-row eyebrow="Box leagues" title="Box leagues that run themselves" flip>
        <p>Split everyone into divisions and we lay out a round-robin grid for every box. Players enter their
          own scores, opponents approve with a tap, and the table recalculates the instant a result lands.</p>
        <p>Run in cycles with automatic promotion and relegation, bonus points for finishing your matches, and
          a one-tap PDF export for the clubhouse noticeboard.</p>
        <template #actions>
          <v-btn color="primary" rounded="pill" to="/register">Start a box league</v-btn>
        </template>
        <template #media>
          <app-frame caption="Division 1 · live standings" tone="brand" :tilt="1.5">
            <league-cycle-division-matrix v-model="leagueDivisionDemo"/>
          </app-frame>
        </template>
      </feature-row>

      <feature-row eyebrow="Ladders" title="Challenge ladders that keep everyone playing">
        <p>Give every member a place on the ladder and a reason to keep playing. Challenge someone above you, win, and take their spot.
          You decide how far anyone can reach, and players heading away can freeze their place.</p>
        <p>Challenges expire on your schedule, and you choose the penalty for ignoring one: nothing, an automatic
          loss, or a drop down the ladder.</p>
        <template #actions>
          <v-btn color="primary" rounded="pill" to="/register">Start a ladder</v-btn>
        </template>
        <template #media>
          <app-frame caption="Singles Ladder · live" tone="ladder" :tilt="-1.5">
            <ladder-view :ladder="ladderDemo"/>
          </app-frame>
        </template>
      </feature-row>

      <feature-row eyebrow="Tournaments" title="Knockout brackets that fill themselves in" flip>
        <p>Run a knockout from the first round to the final, or play round-robin groups first and feed the
          knockout from group positions. Byes are handled automatically and seeding is up to you.</p>
        <p>The bracket updates live as results come in, so everyone at the club can follow the day on their phone.</p>
        <template #actions>
          <v-btn color="primary" rounded="pill" to="/register">Run a tournament</v-btn>
        </template>
        <template #media>
          <app-frame caption="Summer Open · semi → final" tone="tournament" :tilt="1.5">
            <div class="mk-bracket-demo">
              <bracket-knockout-stage :stage="bracketSemiDemo" :round="1" :total-rounds="2"/>
              <bracket-knockout-stage :stage="bracketFinalDemo" :round="2" :total-rounds="2"/>
            </div>
          </app-frame>
        </template>
      </feature-row>

      <feature-row eyebrow="Americano leagues" title="Enter solo, partner everyone">
        <p>The social-but-competitive doubles format taking padel and tennis clubs by storm: enter on your own
          and the rotation pairs you with every player in your division exactly once, so the standings measure
          you, not your partner.</p>
        <p>Divisions of 4, 8 or 12 keep games at your level, every match exists the moment the cycle starts, and
          promotion and relegation happen per player when it rolls over.</p>
        <template #actions>
          <v-btn color="primary" rounded="pill" to="/register">Start an americano league</v-btn>
        </template>
        <template #media>
          <app-frame caption="Division 1 · per-player standings" tone="brand" :tilt="-1.5">
            <americano-standings-table :players="americanoStandingsDemo.players" has-set-scores/>
          </app-frame>
        </template>
      </feature-row>

      <feature-row eyebrow="Meets" title="Club night, organised" flip>
        <p>Turn the recurring social night into a one-tap RSVP: members join once, say yes to the dates they can
          make, and when someone drops out the next on the waitlist is offered the spot.</p>
        <p>On the night you register arrivals, pair everyone up and record results from one console, with paid
          sessions billing themselves after play.</p>
        <template #actions>
          <v-btn color="primary" rounded="pill" to="/register">Start a meet</v-btn>
        </template>
        <template #media>
          <app-frame caption="Wednesday mixer · next dates" tone="brand" :tilt="1.5">
            <div class="mk-demo-static">
              <meet-occurrence-card
                  :meet="meetDemo.meet"
                  :occurrence="meetDemo.occurrences[0]"
                  :my-attendance="meetDemo.myAttendance"
              />
              <meet-occurrence-card
                  :meet="meetDemo.meet"
                  :occurrence="meetDemo.occurrences[1]"
                  :my-attendance="null"
              />
            </div>
          </app-frame>
        </template>
      </feature-row>

      <feature-row eyebrow="Scores &amp; approvals" title="Players enter scores, opponents approve">
        <p>No more chasing results. Either player enters the score from their phone, their opponent taps to
          approve, and standings, ladder positions and brackets all update automatically.</p>
        <p>Scoring is configurable per sport and per competition, with game-by-game breakdowns and proposed
          match dates your opponent can confirm.</p>
        <template #media>
          <app-frame caption="Live matches" tone="brand" :tilt="-1.5">
            <div class="mk-fixture-stack">
              <fixture-cell :fixture="playedFixtureDemo" :navigable="false" :allow-editing="false" :bordered="true"/>
              <fixture-cell :fixture="heroFixtureDemo" :navigable="false" :allow-editing="false" :bordered="true"/>
            </div>
          </app-frame>
        </template>
      </feature-row>

      <!-- ===== CLUB STATS SHOWCASE ===== -->
      <section class="mk-stats">
        <div class="mk-stats-head">
          <span class="mk-eyebrow">Club stats</span>
          <h2 class="mk-stats-title">See exactly who's on form</h2>
          <p class="mk-stats-sub">Every result feeds live leaderboards: recent form, win rates, activity and
            all-time records for your whole club.</p>
        </div>
        <app-frame caption="Club stats · live leaderboard" tone="ink" :tilt="0">
          <stats-view :stats="statsDemo" form-mode="recent"/>
        </app-frame>
      </section>

      <!-- ===== APP PARITY ===== -->
      <app-callout class="mk-block">
        <template #screen>
          <app-home-preview/>
        </template>
      </app-callout>

      <!-- ===== FOR ORGANISERS ===== -->
      <section class="mk-section">
        <div class="mk-section-intro">
          <div class="tl-label text-primary">For organisers</div>
          <h2>Everything you need to run the club</h2>
          <p class="mk-lead">Create your club once and let it run itself, with members and competitions managed
            without spreadsheets, group chats or whiteboards.
            <NuxtLink to="/features" class="mk-lead-link">See every feature</NuxtLink></p>
        </div>
        <div class="mk-org-grid">
          <div class="tl-card mk-org-tile">
            <v-icon color="primary" size="20">mdi-account-group</v-icon>
            <h3>Members &amp; roles</h3>
            <p>Review join requests, manage member details and grant admin rights to the people who help you run things.</p>
          </div>
          <div class="tl-card mk-org-tile">
            <v-icon color="primary" size="20">mdi-link-variant</v-icon>
            <h3>Invite links</h3>
            <p>Share a single link that lets players join your club, with optional approval so you stay in control of who gets in.</p>
          </div>
          <div class="tl-card mk-org-tile">
            <v-icon color="primary" size="20">mdi-bullhorn-outline</v-icon>
            <h3>Announcements</h3>
            <p>Post club announcements so nobody misses the new season, the sign-up deadline or tournament day.</p>
          </div>
          <div class="tl-card mk-org-tile">
            <v-icon color="primary" size="20">mdi-email-multiple-outline</v-icon>
            <h3>Email tools</h3>
            <p>Send emails to all of your members straight from the club admin area. No exporting address lists.</p>
          </div>
          <div class="tl-card mk-org-tile">
            <v-icon color="primary" size="20">mdi-scoreboard-outline</v-icon>
            <h3>Custom scoring systems</h3>
            <p>Define scoring per sport and per competition: outcomes, points and bonuses that match how your club actually plays.</p>
          </div>
          <div class="tl-card mk-org-tile">
            <v-icon color="primary" size="20">mdi-file-pdf-box</v-icon>
            <h3>PDF exports</h3>
            <p>Export ladders, league tables and results to PDF for the clubhouse noticeboard.</p>
          </div>
        </div>
      </section>

      <!-- Testimonials -->
      <testimonial-display class="mt-4"/>

      <!-- ===== FINAL CTA ===== -->
      <div class="mk-final">
        <div class="mk-final-streaks" aria-hidden="true"/>
        <div class="mk-final-inner">
          <h2 class="mk-final-title">Your first competition is minutes away</h2>
          <p class="mk-final-sub">Free for players and clubs. Set it up, send the link, and let the bragging rights begin.</p>
          <div class="mk-final-cta">
            <v-btn color="white" class="text-primary px-6" size="large" rounded="pill" prepend-icon="mdi-sword-cross" to="/register">
              Create your free account
            </v-btn>
            <v-btn color="white" variant="outlined" size="large" rounded="pill" to="/clubs">
              Find your club
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Download the app -->
      <download-app-banner class="mt-8"/>

    </v-container>
  </v-app>
</template>

<style scoped lang="scss">
.mk-page {
  max-width: 1100px;
}

/* ghost button: white surface, hairline border, ink text */
.mk-ghost {
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-text));
  border: 1px solid rgb(var(--v-theme-borderLight));
}

.mk-eyebrow {
  font-size: 11.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: rgb(var(--v-theme-primary));
}

/* ---- Plain-English overview lead ---- */
.mk-overview {
  max-width: 760px;
  margin: 8px auto 0;
  text-align: center;

  p {
    font-size: 15px;
    line-height: 1.7;
    color: rgb(var(--v-theme-charcoal));

    strong {
      color: rgb(var(--v-theme-text));
      font-weight: 700;
    }
  }

  @media (min-width: 960px) {
    p { font-size: 16px; }
  }
}

/* ---- Sports support marquee ---- */
.mk-sports {
  padding: 16px 0 8px;

  @media (min-width: 960px) {
    padding: 24px 0 8px;
  }
}

/* ---- Bracket / fixture demos inside frames ---- */
.mk-bracket-demo {
  display: flex;
  gap: 28px;
  min-width: 560px;
}

.mk-fixture-stack {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* static (non-interactive) demo cards inside a frame */
.mk-demo-static {
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ---- Stats showcase ---- */
.mk-stats {
  margin-top: 72px;

  @media (min-width: 960px) {
    margin-top: 100px;
  }
}

.mk-stats-head {
  text-align: center;
  max-width: 600px;
  margin: 0 auto 28px;
}

.mk-stats-title {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.5px;
  margin-top: 6px;

  @media (min-width: 960px) {
    font-size: 30px;
  }
}

.mk-stats-sub {
  font-size: 14px;
  color: rgb(var(--v-theme-charcoal));
  line-height: 1.6;
  margin-top: 8px;
}

.mk-block {
  margin-top: 72px;

  @media (min-width: 960px) {
    margin-top: 100px;
  }
}

/* ---- Full-width content sections ---- */
.mk-section {
  padding: 44px 0;

  .mk-section-intro {
    max-width: 760px;

    h2 {
      font-size: 22px;
      font-weight: 800;
      letter-spacing: -0.5px;
      margin-top: 8px;
    }
  }

  .mk-lead {
    font-size: 13.5px;
    color: rgb(var(--v-theme-charcoal));
    line-height: 1.7;
    margin-top: 12px;
  }
}

/* The one inbound link to /features on the home page. */
.mk-lead-link {
  display: inline-block;
  margin-left: 6px;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}

/* ---- Organiser tiles ---- */
.mk-org-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  margin-top: 18px;

  @media (min-width: 600px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 960px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.mk-org-tile {
  padding: 18px;

  h3 {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.2px;
    margin-top: 10px;
  }

  p {
    font-size: 12.5px;
    color: rgb(var(--v-theme-charcoal));
    line-height: 1.6;
    margin-top: 6px;
  }
}

/* ---- Final CTA (brand band) ---- */
.mk-final {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  margin-top: 72px;
  padding: 56px 30px;
  text-align: center;
  color: #fff;
  background: linear-gradient(135deg, #E0314F 0%, #D32946 44%, #A31C35 100%);
  box-shadow: 0 30px 60px -30px rgba(163, 28, 53, 0.75);

  @media (min-width: 960px) {
    margin-top: 100px;
    padding: 72px 40px;
  }
}

.mk-final-streaks {
  position: absolute;
  inset: 0;
  z-index: 0;
  background: repeating-linear-gradient(118deg, transparent 0 38px, rgba(255, 255, 255, 0.05) 38px 40px);
}

.mk-final-inner {
  position: relative;
  z-index: 1;
}

.mk-final-title {
  font-size: 26px;
  font-weight: 900;
  letter-spacing: -0.8px;

  @media (min-width: 960px) {
    font-size: 36px;
  }
}

.mk-final-sub {
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  margin-top: 10px;
}

.mk-final-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 26px;
}
</style>
